import React from "react";
import { Guid } from "guid-typescript";

import { DropDownOption } from "./DropDown";

import "./SelectList.scss";

interface SelectListProps {
    id?: string;
    label?: string;
    value?: string;
    size?: number;
    tabs?: boolean;
    disabled?: boolean;
    className?: string;
    tooltip?: string;
    tooltipPlace?: string;
    options: DropDownOption[];
    onChange?: (value: any) => void;
}

interface SelectListState {
    id: string;
    typeBuffer: string;
    displayValue: string;
}

class SelectList extends React.Component<SelectListProps, SelectListState> {
    alphaNumeric =
        "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789.-/\\".split(
            "",
        );

    state = {
        id: Guid.create().toString().split("-")[0],
        typeBuffer: "",
        displayValue: "",
    };

    componentDidMount(): void {
        this.ensureSelectionVisible();
    }

    componentDidUpdate(
        prevProps: Readonly<SelectListProps>,
        prevState: Readonly<SelectListState>,
        snapshot?: any,
    ): void {
        this.ensureSelectionVisible();
    }

    ensureSelectionVisible() {
        for (const option of this.props.options)
            if (!option.ref) option.ref = React.createRef<HTMLLIElement>();

        this.props.options
            .find(item => item.value === this.props.value)
            ?.ref?.current?.scrollIntoView({
                block: "nearest",
            });
    }

    handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.key) {
            case "Enter":
            case " ":
            case "ArrowUp":
            case "ArrowDown":
            case "ArrowLeft":
            case "ArrowRight":
                event.stopPropagation();
                event.preventDefault();
                break;
        }
    }

    handleKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.key) {
            case "Enter":
            case " ":
                event.stopPropagation();
                event.preventDefault();
                break;
            case "ArrowDown":
                this.props.options
                    .sort(this.optionSort.bind(this))
                    .filter(
                        item =>
                            !item.unselectable && !item.notice && !item.hidden,
                    )[0]
                    .ref?.current?.focus();
                event.stopPropagation();
                event.preventDefault();
                break;
            default:
                if (this.alphaNumeric.includes(event.key)) {
                    const buffer = this.state.typeBuffer + event.key;
                    const option = this.props.options
                        .filter(
                            item =>
                                !item.unselectable &&
                                !item.notice &&
                                !item.hidden,
                        )
                        .find(item =>
                            item.text
                                .toLowerCase()
                                .startsWith(buffer.toLowerCase()),
                        );

                    if (option) {
                        option.ref?.current?.focus();
                        this.setState({ typeBuffer: buffer });
                    }
                }
                break;
        }
    }

    handleOptionKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
        switch (event.key) {
            case "ArrowUp":
            case "ArrowDown":
            case "ArrowLeft":
            case "ArrowRight":
                event.stopPropagation();
                event.preventDefault();
                break;
        }
    }

    handleOptionKeyUp(
        option: DropDownOption,
        event: React.KeyboardEvent<HTMLInputElement>,
    ) {
        switch (event.key) {
            case "Enter":
            case " ":
                this.handleOptionClick(null, option);
                event.stopPropagation();
                event.preventDefault();
                break;
            case "ArrowUp":
                this.prevOption(option)?.ref?.current?.focus();
                event.stopPropagation();
                event.preventDefault();
                break;
            case "ArrowDown":
                this.nextOption(option)?.ref?.current?.focus();
                event.stopPropagation();
                event.preventDefault();
                break;
            default:
                event.stopPropagation();
                event.preventDefault();
                break;
        }
    }

    prevOption(current: DropDownOption) {
        const index = this.props.options.indexOf(current);
        return this.props.options
            .sort(this.optionSort.bind(this))
            .slice(0, index)
            .filter(item => !item.unselectable && !item.notice && !item.hidden)
            .pop();
    }

    nextOption(current: DropDownOption) {
        const index = this.props.options.indexOf(current);
        return this.props.options
            .sort(this.optionSort.bind(this))
            .slice(index + 1)
            .filter(
                item => !item.unselectable && !item.notice && !item.hidden,
            )[0];
    }

    handleOptionClick(
        event: React.MouseEvent<HTMLLIElement> | null,
        option: DropDownOption,
    ) {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        if (this.props.disabled || option.unselectable || option.notice) {
            return;
        }

        this.setState({ displayValue: option.text });
        this.props.onChange?.(option.value);
    }

    optionSort(a: DropDownOption, b: DropDownOption) {
        return a.order && b.order && a.order !== b.order
            ? (a.order ?? 0) - (b.order ?? 0)
            : a.text.localeCompare(b.text);
    }

    render(): React.ReactNode {
        this.ensureSelectionVisible();

        let classes = "select-list";

        if (this.props.className) classes += ` ${this.props.className}`;
        if (this.props.tabs) classes += " tabs";

        return (
            <>
                <label
                    className={classes}
                    data-html={true}
                    data-tip={this.props.tooltip}
                    data-place={this.props.tooltipPlace}>
                    {this.props.label && (
                        <span className="text">{this.props.label}</span>
                    )}
                    <ul
                        className={`options`}
                        style={{
                            height: this.props.size
                                ? `${this.props.size * 30}px`
                                : "auto",
                        }}>
                        {this.props.options
                            .sort(this.optionSort.bind(this))
                            .map((option, index) =>
                                option.hidden ? null : (
                                    <li
                                        key={index}
                                        ref={option.ref}
                                        tabIndex={
                                            this.props.disabled ||
                                            option.unselectable ||
                                            option.notice
                                                ? -1
                                                : 0
                                        }
                                        className={`option ${
                                            option.className || ""
                                        }${
                                            this.props.disabled ||
                                            option.unselectable
                                                ? " unselectable"
                                                : ""
                                        }${option.notice ? " notice" : ""}${
                                            option.static ? " static" : ""
                                        }${
                                            this.props.value === option.value
                                                ? " selected"
                                                : ""
                                        }`}
                                        onKeyDown={this.handleOptionKeyDown.bind(
                                            this,
                                        )}
                                        onKeyUp={this.handleOptionKeyUp.bind(
                                            this,
                                            option,
                                        )}
                                        onClick={event =>
                                            this.handleOptionClick(
                                                event,
                                                option,
                                            )
                                        }>
                                        {option.text}
                                    </li>
                                ),
                            )}
                    </ul>
                </label>
            </>
        );
    }
}

export default SelectList;
